import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCapIcon } from 'lucide-react';
import { findDegreesTemplates } from '../data/findDegreesTemplates';
import { guides } from '../data/guides';
import { articles } from '../data/articles';
import { programTemplates, programsIndexSlug } from '../data/programTemplates';
import { degreeCategories } from '../data/degrees';
import { universities } from '../data/universities';
import { FooterTemplateDropdown, type TemplateLink } from './FooterTemplateDropdown';
function slugToLabel(slug: string): string {
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/**
 * Every route template on the site, paired with the complete set of URLs it
 * currently serves. Built from the same data the pages render from, so a new
 * entry appears here automatically rather than needing a hand-written link.
 */
const routeTemplates: {
  label: string;
  pattern: string;
  links: TemplateLink[];
}[] = [{
  label: 'Guides',
  pattern: '/guide/:slug',
  links: guides.map(guide => ({
    label: guide.title,
    to: `/guide/${guide.slug}`
  }))
}, {
  label: 'Articles',
  pattern: '/article/:slug',
  links: articles.map(article => ({
    label: article.title,
    to: `/article/${article.slug}`
  }))
}, {
  label: 'Program Information',
  pattern: '/program-information/:template',
  links: [{
    label: 'All New Online Programs',
    to: `/program-information/${programsIndexSlug}`
  }, ...programTemplates.map(program => ({
    label: `${program.name} (${program.credential})`,
    to: `/program-information/${program.slug}`
  }))]
}, {
  label: 'Degree Categories',
  pattern: '/degrees/:category',
  links: degreeCategories.map(category => ({
    label: category.name,
    to: `/degrees/${category.id}`
  }))
}, {
  label: 'Partner Colleges',
  pattern: '/colleges/:universitySlug',
  links: universities.map(university => ({
    label: university.name,
    to: `/colleges/${university.slug}`
  }))
}, {
  label: 'Theme Landing Pages',
  pattern: '/find-degrees/:template',
  links: findDegreesTemplates.map(template => ({
    label: slugToLabel(template.slug),
    to: `/find-degrees/${template.slug}`
  }))
}];
export function Footer() {
  const currentYear = new Date().getFullYear();
  const scrollToTop = () => window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  return <footer className="bg-navy-dark text-white pt-16 pb-8" data-id="element-208" data-mp="1hms6xz46bcx9">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-209" data-mp="1mktq0k1lnc8q">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12" data-id="element-210" data-mp="1riv9h62xtdkq">
          {/* Brand Col */}
          <div className="col-span-1 md:col-span-1" data-id="element-211" data-mp="3obev1m2xt08p">
            <Link to="/" onClick={scrollToTop} className="flex items-center mb-4" data-id="element-212" data-mp="3ocq0dqc16g1x">
              <GraduationCapIcon className="h-8 w-8 text-gold-light mr-2" data-id="element-213" data-mp="2af3srq70x3jm" />
              <span className="text-xl font-bold text-white tracking-tight" data-id="element-214" data-mp="2ah84uvf2t8uz">
                Online College Guide
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6" data-id="element-215" data-mp="21m0kdwb8jgvu">
              Empowering students to find the perfect online degree program to
              advance their careers and achieve their goals.
            </p>
          </div>

          {/* Links Col 1 */}
          <div data-id="element-216" data-mp="2pcnrwgh8o4hv">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-4" data-id="element-217" data-mp="2pbuqlv11u774">
              Top Programs
            </h3>
            <ul className="space-y-3 mb-6" data-id="element-218" data-mp="2kbqcucrizqkl">
              <li data-id="element-219" data-mp="2fegpr3rlzqzx">
                <Link to="/degrees/business" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-220" data-mp="2fezvzn7wcqy4">
                  Business Administration
                </Link>
              </li>
              <li data-id="element-221" data-mp="2rv7q80hgq42d">
                <Link to="/degrees/computer-science" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-222" data-mp="2ruogv85vonzy">
                  Computer Science
                </Link>
              </li>
              <li data-id="element-223" data-mp="2ntcs14utw6st">
                <Link to="/degrees/healthcare" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-224" data-mp="2nuxh4eydixja">
                  Nursing & Healthcare
                </Link>
              </li>
              <li data-id="element-225" data-mp="31khknmzs3ytn">
                <Link to="/degrees/psychology" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-226" data-mp="31larsp2ngqcb">
                  Psychology
                </Link>
              </li>
            </ul>

            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-4" data-id="element-227" data-mp="1v71n5i06vjq7">
              Get Started
            </h3>
            <ul className="space-y-3" data-id="element-228" data-mp="1imv11rpfzj71">
              <li data-id="element-229" data-mp="1iltd4mnbv27w">
                <Link to="/request-information" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-230" data-mp="1imcthyxbxijt">
                  Request Information
                </Link>
              </li>
              <li data-id="element-231" data-mp="13yrtidvr3zg4">
                <Link to="/get-matched" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-232" data-mp="13y898qimoyyx">
                  Find My Match
                </Link>
              </li>
            </ul>
          </div>

          {/* Links Col 2 */}
          <div data-id="element-233" data-mp="l77dscgpvlk4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-4" data-id="element-234" data-mp="l6p68joltkww">
              Resources
            </h3>
            <ul className="space-y-3 mb-6" data-id="element-235" data-mp="g7updna766ff">
              <li data-id="element-236" data-mp="2gawz3rxoby70">
                <Link to="/colleges" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-237" data-mp="2gbf3xcyejkqj">
                  Partner Colleges
                </Link>
              </li>
            </ul>
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-2" data-id="element-238" data-mp="2v01i3bcam6g6">
              Guides
            </h3>
            <p className="text-xs text-gray-500 mb-3" data-id="element-239" data-mp="1xd2sfkk0pyrs">
              Every page each template serves.
            </p>
            <div className="space-y-2" data-id="element-240" data-mp="22b4co8t6k44u">
              {routeTemplates.map(template => <FooterTemplateDropdown key={template.pattern} label={template.label} pattern={template.pattern} links={template.links} data-id="element-241" data-mp="278nqj7lyuew9" />)}
            </div>
          </div>

          {/* Legal Col */}
          <div data-id="element-242" data-mp="3mowbt5vj4nks">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-300 mb-4" data-id="element-243" data-mp="3msk4k2t5h5yg">
              Legal
            </h3>
            <ul className="space-y-3" data-id="element-244" data-mp="1718vmpel0n6b">
              <li data-id="element-245" data-mp="172akbwoit86i">
                <Link to="/privacy-policy" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-246" data-mp="173ve3k30gn3b">
                  Privacy Policy
                </Link>
              </li>
              <li data-id="element-247" data-mp="sefoi7f7unp7">
                <Link to="/terms-and-conditions" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-248" data-mp="sarw5blic7c2">
                  Terms of Service
                </Link>
              </li>
              <li data-id="element-249" data-mp="eexundn2ck8z">
                <Link to="/cookie-policy" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-250" data-mp="efg2l7j38mwq">
                  Cookie Policy
                </Link>
              </li>
              <li data-id="element-251" data-mp="1hmk0lxz9sm1d">
                <Link to="/do-not-sell-my-info" onClick={scrollToTop} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-252" data-mp="1ho4lswzp2smk">
                  Do Not Sell My Info
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8" data-id="element-253" data-mp="yv0zxxspxk2i">
          <div className="text-xs text-gray-400 space-y-4 text-center md:text-left" data-id="element-254" data-mp="yxdctwjmd48p">
            <p data-id="element-255" data-mp="12y748zw9t16c">
              <strong data-id="element-256" data-mp="12uj9xyj04aqk">Advertiser Disclosure:</strong> OnlineCollegeGuide.com is
              an advertising-supported site. Featured or trusted partner
              programs and all school search, finder, or match results are for
              schools that compensate us. This compensation does not influence
              our school rankings, resource guides, or other
              editorially-independent information published on this site.
            </p>
            <p data-id="element-257" data-mp="cnn7gzh8z7u5">
              <strong data-id="element-258" data-mp="cn51hb4s9f91">TCPA Disclosure:</strong> By submitting your information
              on this website, you consent to be contacted by our educational
              partners regarding their programs via email, telephone, or text
              message.
            </p>
            <p className="pt-4 text-gray-500" data-id="element-259" data-mp="hkx16rbo65ns">
              &copy; {currentYear} OnlineCollegeGuide. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>;
}